import { useState } from "react";
import { type API, type JsonResponse } from "../logic/classApi";
import { createUrl, formatApiDateString, formatApiYmdString } from "./apiCommon";
import { showErrorMessage, showSuccessMessage } from "../components/appPane";
import { MESSAGES } from "../constants/const";

/** 焼却量データ情報APIのURL */
const API_PROCESSPLAN_URL = import.meta.env.VITE_API_PROCESSPLAN_URL;

/** 焼却量データ情報 */
interface ProcessPlanData {
  /** 支所コード */
  branchCode: string;
  /** 炉番号 */
  furnace: number;
  /** 計画日 */
  processPlanDate: string;
  /** 焼却量 */
  processPlanData: number;
  /** 実績焼却量 */
  processResultData?: number;
  /** 作成日時 */
  createdAt?: string;
  /** 更新日時 */
  updatedAt?: string;
  /** 削除日時 */
  deletedAt?: string;
}

/**
 * 焼却量データ情報API
 * @param api APIモジュール
 * @param branchCode 支所コード
 * @param furnaceCount 炉数
 * @returns
 */
export function useProcessPlanApi(api: API, branchCode: string, furnaceCount: string) {
  const [plans, setPlans] = useState<ProcessPlanData[]>();

  /**
   * 焼却量データ情報取得
   * @param startDate 開始日付
   * @param endDate 終了日付
   */
  async function loadPlans(startDate: Date, endDate: Date) {
    const params = {
      branchCode,
      furnaceCount,
      startDate: formatApiDateString(startDate),
      endDate: formatApiDateString(endDate),
    };
    const reqUrl = createUrl(API_PROCESSPLAN_URL, params);
    try {
      const result = await api.getRequest<ProcessPlanData[]>(reqUrl);
      setPlans(result.payload);
    } catch (e) {
      showErrorMessage(MESSAGES.ERROR_GET);
    }
  }

  async function uploadPlans(fiscalYear: string, file: File) {
    const params = {
      branchCode,
      furnaceCount,
      fiscalYear,
    };
    const reqUrl = createUrl(API_PROCESSPLAN_URL, params);
    try {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = async e => {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const uint8Array = new Uint8Array(arrayBuffer);
        await api.postRequestBinary<JsonResponse>(reqUrl, uint8Array);
        showSuccessMessage(MESSAGES.SUCCESS_PROCESS_PLAN_UPLOAD);
      };
    } catch (e) {
      console.error(e);
      showErrorMessage(MESSAGES.ERROR_POST);
    }
  }

  /**
   * 計画焼却量合計値取得
   * @param date 取得対象日
   * @returns 焼却量合計値
   */
  const getProcessPlanDataSum = (date: Date) => {
    const ymd = formatApiYmdString(date);
    return (
      plans
        ?.filter(p => p.processPlanDate === ymd)
        .reduce((sum, p) => sum + (p.processPlanData ?? 0), 0) ?? 0
    );
  };

  return [
    { plans, getProcessPlanDataSum },
    { loadPlans, uploadPlans },
  ];
}
